import React, { useEffect, useMemo, useState } from 'react';
import Swal from '../librarianSwal';
import Barcode from 'react-barcode';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';

import {
  getMembersByLibrary,
  getErrorMessage
} from '../../api/api';

import '../BookList.css';
import '../lib-page-header.css';
import FilterSelect from '../components/FilterSelect';
import Pagination from '../components/Pagination';

const PAGE_SIZE = 10;

const LibraryCardList = () => {
  const libraryId = useActiveLibraryId();

  const [members, setMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const safeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const nameOf = (m) => {
    const fullName =
      m?.full_name ||
      m?.member_name ||
      m?.parent_name ||
      m?.name ||
      `${m?.first_name || ''} ${m?.last_name || ''}`.trim();

    return fullName || '-';
  };

  const mobileOf = (m) => {
    return m?.mobile || m?.phone || m?.mobile_no || '-';
  };

  const typeOf = (m) => {
    return (
      m?.member_type ||
      m?.membership_type ||
      m?.member_source ||
      m?.membership_source ||
      '-'
    );
  };

  const statusOf = (m) => {
    return (
      m?.status ||
      m?.membership_status ||
      m?.active_membership_status ||
      '-'
    );
  };

  /*
    IMPORTANT:
    Backend ko change nahi karna hai, isliye card barcode me ab LIB-{member.id}
    nahi bhej rahe. Backend memberships.membership_no se member find karega.

    DB example:
    members.id = 12
    memberships.membership_no = SP-1-12

    Isliye card scan value:
    SP-1-12

    Agar API se membership_no aa raha hai to wahi use hoga.
    Agar API me membership_no missing hai, to member_type + libraryId + member.id
    se code generate hoga:
      school_parent => SP-1-12
      outsider      => OUT-1-10
      student       => STU-1-10
      staff/teacher => STF-1-10
      fallback      => LIB-1-10
  */
  const getMemberCardCode = (member) => {
    const directCode =
      member?.membership_no ||
      member?.membershipNo ||
      member?.card_no ||
      member?.library_card_no ||
      member?.active_membership_no;

    if (directCode) {
      return String(directCode).trim();
    }

    const type = String(typeOf(member) || '').toLowerCase();
    const libId = member?.library_id || libraryId || 1;
    const memberId = member?.id || member?.member_id || '';

    let prefix = 'LIB';

    if (type.includes('school_parent') || type.includes('parent')) {
      prefix = 'SP';
    } else if (type.includes('outsider')) {
      prefix = 'OUT';
    } else if (type.includes('student')) {
      prefix = 'STU';
    } else if (type.includes('staff') || type.includes('teacher')) {
      prefix = 'STF';
    }

    return `${prefix}-${libId}-${memberId}`;
  };

  const fetchMembers = async () => {
    try {
      if (!libraryId) return;

      console.log('[LIBRARY_CARD][FETCH_MEMBERS][REQUEST]', { libraryId });

      const data = await getMembersByLibrary(libraryId);
      const rows = safeArray(data);

      console.log('[LIBRARY_CARD][FETCH_MEMBERS][RESPONSE]', rows);
      console.log(
        '[LIBRARY_CARD][CARD_CODES]',
        rows.map((member) => ({
          id: member.id,
          name: nameOf(member),
          member_type: typeOf(member),
          membership_no: member.membership_no,
          generated_card_code: getMemberCardCode(member)
        }))
      );

      setMembers(rows);
    } catch (error) {
      console.error('[LIBRARY_CARD][FETCH_MEMBERS][ERROR]', {
        error,
        response: error?.response?.data,
        message: error?.message
      });

      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectedMembers = useMemo(() => {
    return members.filter((member) => selectedIds.includes(member.id));
  }, [members, selectedIds]);

  const handlePrint = () => {
    if (selectedIds.length === 0) {
      Swal.fire('Error', 'Please select members to print card', 'error');
      return;
    }

    console.log(
      '[LIBRARY_CARD][PRINT_SELECTED]',
      selectedMembers.map((member) => ({
        id: member.id,
        name: nameOf(member),
        card_code: getMemberCardCode(member)
      }))
    );

    window.print();
  };

  const initials = (name) =>
    (String(name).match(/\b\w/g) || [])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'M';

  const typePill = (t) => {
    const k = String(t || '').toLowerCase();

    if (k.includes('parent')) return 'green';
    if (k.includes('outsider')) return 'amber';
    if (k.includes('student')) return 'blue';
    if (k.includes('staff') || k.includes('teacher')) return 'slate';

    return 'slate';
  };

  const statusPill = (s) =>
    String(s || '').toLowerCase() === 'active' ? 'green' : 'red';

  const filteredMembers = members.filter((member) => {
    if (typeFilter) {
      const t = String(typeOf(member) || '').toLowerCase();
      if (!t.includes(typeFilter)) return false;
    }
    if (statusFilter) {
      const s = String(statusOf(member) || '').toLowerCase();
      if (s !== statusFilter) return false;
    }
    const keyword = search.toLowerCase();
    if (!keyword) return true;
    const cardCode = getMemberCardCode(member);
    return (
      String(nameOf(member)).toLowerCase().includes(keyword) ||
      String(mobileOf(member)).toLowerCase().includes(keyword) ||
      String(member.email || '').toLowerCase().includes(keyword) ||
      String(typeOf(member)).toLowerCase().includes(keyword) ||
      String(cardCode).toLowerCase().includes(keyword)
    );
  });

  const allSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((member) => selectedIds.includes(member.id));

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
  const paginatedMembers = filteredMembers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="bd-page">
      <style>{`
        @media print {
          @page { margin: 10mm; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }
          .lc-card { page-break-inside: avoid; box-shadow: none; }
        }

        .lc-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          padding: 4px 0;
        }

        .lc-card,
        .lc-card * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .lc-card {
          width: 330px;
          background: #fff;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid #e7eef7;
          box-shadow: 0 12px 30px rgba(15, 23, 42, .16);
        }

        .lc-head {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 16px 10px;
        }

        .lc-head .lc-logo {
          width: 26px;
          height: 26px;
          border-radius: 7px;
          color: #fff;
          font-size: 15px;
          background: linear-gradient(135deg, #f59e0b, #14b8a6);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .lc-head .lc-uni {
          font-size: 15px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: .02em;
        }

        .lc-photo {
          position: relative;
          text-align: center;
          padding: 16px 0 30px;
          background: linear-gradient(160deg, #22d3ee 0%, #0ea5e9 55%, #2563eb 125%);
          border-radius: 0 0 46% 46% / 0 0 30px 30px;
        }

        .lc-avatar {
          position: absolute;
          left: 50%;
          bottom: 0;
          transform: translate(-50%, 50%);
          width: 92px;
          height: 92px;
          border-radius: 50%;
          background: #fff;
          border: 4px solid #fff;
          box-shadow: 0 6px 16px rgba(2, 28, 55, .25);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: 800;
          color: #0ea5e9;
        }

        .lc-body {
          padding: 52px 18px 6px;
          text-align: center;
        }

        .lc-name {
          font-size: 19px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: .03em;
        }

        .lc-dept {
          font-size: 11.5px;
          font-weight: 700;
          color: #0ea5e9;
          text-transform: uppercase;
          letter-spacing: .06em;
          margin-top: 3px;
        }

        .lc-course {
          font-size: 10.5px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: .05em;
          margin-top: 1px;
        }

        .lc-grid2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 9px 14px;
          text-align: left;
          margin: 14px 4px 0;
        }

        .lc-item {
          min-width: 0;
        }

        .lc-item.full {
          grid-column: 1 / -1;
        }

        .lc-item label {
          display: block;
          font-size: 8.5px;
          color: #94a3b8;
          font-weight: 700;
          letter-spacing: .09em;
          text-transform: uppercase;
          margin-bottom: 1px;
        }

        .lc-item span {
          font-size: 12px;
          color: #0f172a;
          font-weight: 600;
          word-break: break-word;
        }

        .lc-sign {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 10px;
          padding: 14px 16px 12px;
        }

        .lc-reg {
          text-align: center;
        }

        .lc-reg .sig {
          font-family: 'Segoe Script', 'Brush Script MT', cursive;
          font-size: 16px;
          color: #1e3a8a;
          line-height: 1;
        }

        .lc-reg .line {
          border-top: 1px solid #cbd5e1;
          margin-top: 3px;
          padding-top: 3px;
          font-size: 8.5px;
          color: #64748b;
          letter-spacing: .1em;
          text-transform: uppercase;
        }

        .lc-qr {
          background: #fff;
          padding: 3px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          line-height: 0;
          max-width: 150px;
          overflow: hidden;
        }

        .lc-bar {
          height: 24px;
          background: linear-gradient(90deg, #0f172a, #1e3a8a 60%, #0ea5e9);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .lc-bar span {
          color: #cbd5e1;
          font-size: 9px;
          letter-spacing: .14em;
          text-transform: uppercase;
        }
      `}</style>

      <div className="no-print">
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon">
              <i className="fas fa-id-card" />
            </div>
            <div>
              <h3 className="lib-page-header__title">Library Cards</h3>
              <div className="lib-page-header__subtitle">Select members and print their library cards</div>
            </div>
          </div>
          <div className="lib-page-header__actions">
            <button className="bd-btn bd-btn-primary" onClick={handlePrint}>
              <i className="ti ti-printer" /> Print Selected
            </button>
          </div>
        </div>

        <div className="book-filter-card">
          <div className="book-filter-row">
            <input
              type="text"
              className="book-search-input"
              style={{ flex: '2 1 200px' }}
              placeholder="Search member, mobile, email, card no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterSelect
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: '', label: 'All Types' },
                { value: 'parent', label: 'Parent' },
                { value: 'student', label: 'Student' },
                { value: 'outsider', label: 'Outsider' },
                { value: 'staff', label: 'Staff' },
              ]}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
            <button
              type="button"
              className="book-reset-btn"
              onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter(''); setSelectedIds([]); }}
            >
              Reset
            </button>
          </div>

          {selectedIds.length > 0 && (
            <div className="book-filter-chips">
              <span className="book-filter-chip">
                <strong>{selectedIds.length}</strong> selected
              </span>
            </div>
          )}
        </div>

        <div className="book-table-card">
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) =>
                        setSelectedIds(
                          e.target.checked
                            ? filteredMembers.map((item) => item.id)
                            : []
                        )
                      }
                    />
                  </th>
                  <th>#</th>
                  <th>Member</th>
                  <th>Card No</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {paginatedMembers.length > 0 ? (
                  paginatedMembers.map((member, index) => {
                    const cardCode = getMemberCardCode(member);

                    return (
                      <tr key={member.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="lc-check"
                            style={{
                              width: 17,
                              height: 17,
                              accentColor: '#2563eb',
                              cursor: 'pointer'
                            }}
                            checked={selectedIds.includes(member.id)}
                            onChange={() => toggleSelect(member.id)}
                          />
                        </td>

                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>

                        <td>
                          <span className="book-title">{nameOf(member)}</span>
                        </td>

                        <td>{cardCode}</td>

                        <td>{mobileOf(member)}</td>

                        <td>{member.email || '-'}</td>

                        <td>
                          <span className={`bd-pill ${typePill(typeOf(member))}`}>
                            {typeOf(member)}
                          </span>
                        </td>

                        <td>
                          <span className={`bd-pill ${statusPill(statusOf(member))}`}>
                            {statusOf(member)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="book-empty">
                      No members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={filteredMembers.length}
            itemLabel="members"
            onPageChange={setPage}
          />
        </div>
      </div>

      <div className="print-area">
        <div className="lc-grid">
          {selectedMembers.map((member) => {
            const cardCode = getMemberCardCode(member);

            return (
              <div className="lc-card" key={member.id}>
                <div className="lc-head">
                  <div className="lc-logo">
                    <i className="ti ti-books" />
                  </div>

                  <div className="lc-uni">JAIPURIA LIBRARY</div>
                </div>

                <div className="lc-photo">
                  <div className="lc-avatar">{initials(nameOf(member))}</div>
                </div>

                <div className="lc-body">
                  <div className="lc-name">{nameOf(member)}</div>
                  <div className="lc-dept">{typeOf(member)}</div>
                  <div className="lc-course">Library Membership Card</div>

                  <div className="lc-grid2">
                    <div className="lc-item">
                      <label>Card No</label>
                      <span>{cardCode}</span>
                    </div>

                    <div className="lc-item">
                      <label>Unique ID</label>
                      <span>{member?.unique_id || '-'}</span>
                    </div>

                    <div className="lc-item">
                      <label>Phone</label>
                      <span>{mobileOf(member)}</span>
                    </div>

                    <div className="lc-item full">
                      <label>Email</label>
                      <span>{member.email || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="lc-sign">
                  <div className="lc-reg">
                    <div className="sig">Authorised</div>
                    <div className="line">Issuing Librarian</div>
                  </div>

                  <div className="lc-qr">
                    <Barcode
                      value={cardCode}
                      format="CODE128"
                      width={1.1}
                      height={34}
                      fontSize={9}
                      margin={0}
                      background="transparent"
                    />
                  </div>
                </div>

                <div className="lc-bar">
                  <span>Jaipuria Group of Educational Institutions</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LibraryCardList;
